import React from 'react';
import { Button, TextField, Select, MenuItem } from '@material-ui/core';
import {
  InlineDateTimePicker,
  MuiPickersUtilsProvider,
} from 'material-ui-pickers';
import LuxonUtils from '@date-io/luxon';
import { DateTime } from 'luxon';

type SearchField = 'userId' | 'organisationId';
type TimeRange = '5m' | '1h' | '1d' | '1w' | '1m' | 'custom';

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: '5m', label: 'Last 5 minutes' },
  { value: '1h', label: 'Last hour' },
  { value: '1d', label: 'Last 24 hours' },
  { value: '1w', label: 'Last week' },
  { value: '1m', label: 'Last month' },
  { value: 'custom', label: 'Custom' },
];

function getCreatedAfterFromRange(range: TimeRange): string | undefined {
  if (range === 'custom') return undefined;
  const now = new Date();
  const offsets: Record<string, number> = {
    '5m': 5 * 60 * 1000,
    '1h': 60 * 60 * 1000,
    '1d': 24 * 60 * 60 * 1000,
    '1w': 7 * 24 * 60 * 60 * 1000,
    '1m': 30 * 24 * 60 * 60 * 1000,
  };
  return new Date(now.getTime() - offsets[range]).toISOString();
}

export interface SearchBarQueryVars {
  userId?: string;
  organisationId?: string;
  operationToken?: string;
  createdAfter?: string;
  createdBefore?: string;
}

interface Props {
  onSearch: (vars: SearchBarQueryVars) => void;
}

interface State {
  searchField: SearchField;
  searchValue: string;
  operationToken: string;
  timeRange: TimeRange;
  customCreatedAfter: string;
  customCreatedBefore: string;
}

class SearchBar extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      searchField: 'userId',
      searchValue: '',
      operationToken: '',
      timeRange: '1d',
      customCreatedAfter: '',
      customCreatedBefore: '',
    };
  }

  handleSubmit = () => {
    const {
      searchField,
      searchValue,
      operationToken,
      timeRange,
      customCreatedAfter,
      customCreatedBefore,
    } = this.state;
    const value = searchValue.trim();
    if (!value) return;

    const vars: SearchBarQueryVars = {};
    vars[searchField] = value;
    if (operationToken.trim()) vars.operationToken = operationToken.trim();

    if (timeRange === 'custom') {
      if (customCreatedAfter.trim()) vars.createdAfter = customCreatedAfter.trim();
      if (customCreatedBefore.trim()) vars.createdBefore = customCreatedBefore.trim();
    } else {
      const createdAfter = getCreatedAfterFromRange(timeRange);
      if (createdAfter) vars.createdAfter = createdAfter;
    }

    if (vars.userId || vars.organisationId) {
      this.props.onSearch(vars);
    }
  };

  render() {
    const {
      searchField,
      searchValue,
      operationToken,
      timeRange,
      customCreatedAfter,
      customCreatedBefore,
    } = this.state;

    return (
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Select
          value={searchField}
          onChange={(e) =>
            this.setState({ searchField: e.target.value as SearchField })
          }
          style={{ minWidth: 180, alignSelf: 'flex-end' }}
        >
          <MenuItem value="userId">User ID</MenuItem>
          <MenuItem value="organisationId">Organisation ID</MenuItem>
        </Select>
        <TextField
          label="Search"
          value={searchValue}
          onChange={(e) => this.setState({ searchValue: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && this.handleSubmit()}
          style={{ width: 400 }}
          inputProps={{ 'data-testid': 'search-input' }}
        />
        <TextField
          label="Operation Token (optional)"
          value={operationToken}
          onChange={(e) => this.setState({ operationToken: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && this.handleSubmit()}
          style={{ width: 300 }}
          inputProps={{ 'data-testid': 'operation-token-input' }}
        />
        <Select
          value={timeRange}
          onChange={(e) =>
            this.setState({ timeRange: e.target.value as TimeRange })
          }
          style={{ minWidth: 160, alignSelf: 'flex-end' }}
        >
          {TIME_RANGE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        {timeRange === 'custom' && (
          <MuiPickersUtilsProvider utils={LuxonUtils}>
            <InlineDateTimePicker
              keyboard
              label="Created After"
              value={customCreatedAfter || null}
              onChange={(date: DateTime | null) =>
                this.setState({
                  customCreatedAfter: date ? date.toISO() : '',
                })
              }
              format="dd/MM/yyyy HH:mm"
              style={{ width: 220 }}
            />
            <InlineDateTimePicker
              keyboard
              label="Created Before"
              value={customCreatedBefore || null}
              onChange={(date: DateTime | null) =>
                this.setState({
                  customCreatedBefore: date ? date.toISO() : '',
                })
              }
              format="dd/MM/yyyy HH:mm"
              style={{ width: 220 }}
            />
          </MuiPickersUtilsProvider>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={this.handleSubmit}
          disabled={!searchValue.trim()}
        >
          Search
        </Button>
      </div>
    );
  }
}

export default SearchBar;
